/**
 * 脚本 API 的主线程实现（M4-09 / DESIGN.md#65-值得从-photoshop-补充的功能性价比排序）。
 *
 * 脚本本身在 Worker 里跑（`sandbox.ts`/`sandbox-worker.ts`），这份文件是
 * 它能调用的方法在**主线程**这一侧的实现——每个方法只读或只经过命令总线
 * 派发，脚本拿不到 `Document`/`TileStore` 的直接引用，全部经过序列化边界。
 *
 * 派发命令复用 M4-04 动作录制的白名单解码器（`decodeStep`）——脚本能触发
 * 的"新建/调整/滤镜"就是那份可序列化命令白名单，不是另开一套。这既省了
 * 重复的校验代码，也天然保证脚本改不出无法回放/无法安全反序列化的命令。
 */
import type { AppStore } from '../store';
import { decodeStep, type RecordedStep } from '../actions/action-recorder';
import { compositeRegion } from '../composite';
import type { Rect } from '../../document/types';

export interface ScriptLayerSnapshot {
  readonly id: string;
  readonly name: string;
  readonly kind: string;
  readonly visible: boolean;
  readonly opacity: number;
  readonly blendMode: string;
}

export interface ScriptDocumentSnapshot {
  readonly id: string;
  readonly width: number;
  readonly height: number;
  readonly layers: readonly ScriptLayerSnapshot[];
}

/** 文档结构快照——只有标量字段，没有 TileHandle/像素引用。 */
export function snapshotDocument(store: AppStore): ScriptDocumentSnapshot | null {
  const document = store.document;
  if (document === null) return null;
  const layers: ScriptLayerSnapshot[] = [];
  const walk = (children: typeof document.root.children): void => {
    for (const layer of children) {
      layers.push({
        id: layer.id as string,
        name: layer.name,
        kind: layer.kind,
        visible: layer.visible,
        opacity: layer.opacity,
        blendMode: layer.blendMode,
      });
      if (layer.kind === 'group') walk(layer.children);
    }
  };
  walk(document.root.children);
  return { id: document.id, width: document.size.width, height: document.size.height, layers };
}

export interface ScriptSelectionSnapshot {
  readonly width: number;
  readonly height: number;
  /** 内容包围盒；`null` = 空选区（base=0 且无内容）或全选（视 base 而定，见 bounds 字段语义同 SelectionMask.contentBounds）。 */
  readonly bounds: { readonly x: number; readonly y: number; readonly width: number; readonly height: number } | null;
  readonly base: 0 | 255;
}

/** 选区快照——只给形状信息（包围盒/base），不倒出整张蒙版字节（体量大且脚本很少需要逐像素读选区）。 */
export function snapshotSelection(store: AppStore): ScriptSelectionSnapshot | null {
  const selection = store.selection;
  if (selection === null) return null;
  return {
    width: selection.width,
    height: selection.height,
    bounds: selection.contentBounds(),
    base: selection.base,
  };
}

export interface ScriptPixelsSnapshot {
  readonly width: number;
  readonly height: number;
  /** RGBA8，直通 alpha，行主序——与 `compositeDocument`/`CompositeResult` 同一约定。 */
  readonly pixels: Uint8Array;
}

const MAX_SCRIPT_PIXELS_AREA = 4096 * 4096;

/**
 * 读取文档某个矩形区域的合成像素（压平后的显示结果，不是单图层原始像素——
 * 脚本要处理的是"画面看起来是什么样"，单图层原始像素已经能通过
 * `dispatch` 派发的命令间接达成，不需要在读接口上重复暴露）。
 */
export function snapshotPixels(store: AppStore, rect: Rect): ScriptPixelsSnapshot {
  const document = store.document;
  if (document === null) throw new Error('没有打开的文档。');
  if (rect.width <= 0 || rect.height <= 0) {
    throw new RangeError('getPixels 的矩形宽高必须为正数。');
  }
  if (rect.width * rect.height > MAX_SCRIPT_PIXELS_AREA) {
    throw new RangeError(`getPixels 的矩形过大（上限 ${MAX_SCRIPT_PIXELS_AREA} 像素）。`);
  }
  const out = new Uint8Array(rect.width * rect.height * 4);
  compositeRegion(document, store.tiles, rect, out);
  return { width: rect.width, height: rect.height, pixels: out };
}

/** 派发一条白名单命令（同 M4-04 的可录制命令集合）。 */
export function dispatchScriptCommand(store: AppStore, step: RecordedStep): void {
  store.dispatch(decodeStep(step));
}
